import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [details, setDetails] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDetails(data);
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 text-blue-600 mx-auto animate-spin" />
          <h1 className="mt-6 font-display font-extrabold text-2xl text-slate-900">Confirming your payment…</h1>
          <p className="mt-2 text-slate-500">Please wait, this only takes a moment.</p>
        </>
      )}

      {state === "paid" && (
        <>
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="mt-6 font-display font-extrabold text-3xl text-slate-900">Payment Successful!</h1>
          <p className="mt-2 text-slate-600">
            Thank you for your order. A confirmation with your batch details and tracking will follow by email.
          </p>
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 py-4 px-6 inline-block">
            {details?.amount_total != null && (
              <div className="text-sm text-slate-500">
                Amount paid:{" "}
                <span className="font-bold text-slate-900">
                  ${Number(details.amount_total).toFixed(2)} {String(details.currency || "AUD").toUpperCase()}
                </span>
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1 font-mono break-all">Ref: {sessionId}</div>
          </div>
          <div className="mt-7">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white font-semibold px-6 py-3 hover:bg-blue-700 transition-colors">
              Continue Shopping <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </>
      )}

      {state === "pending" && (
        <>
          <Loader2 className="h-12 w-12 text-amber-500 mx-auto" />
          <h1 className="mt-6 font-display font-extrabold text-2xl text-slate-900">Payment is processing</h1>
          <p className="mt-2 text-slate-500">
            Your payment is still being confirmed. You'll receive an email once it completes.
          </p>
          <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white font-semibold px-6 py-3 hover:bg-blue-700 transition-colors">
            Continue Shopping <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      )}

      {state === "error" && (
        <>
          <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="mt-6 font-display font-extrabold text-2xl text-slate-900">Something went wrong</h1>
          <p className="mt-2 text-slate-500">We couldn't confirm this payment. If you were charged, contact support.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/cart" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 text-slate-700 font-semibold px-6 py-3 hover:border-blue-400 hover:text-blue-700 transition-colors">
              <ShoppingBag className="h-4 w-4" /> Back to Cart
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white font-semibold px-6 py-3 hover:bg-blue-700 transition-colors">
              Contact Support
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
